import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import SkeletonCard from "@/components/base/SkeletonCard";
import { categories, sortOptions, type CategoryKey } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import { queryVideos, type SortKey } from "@/utils/video";

const hotKeywords = ["纪录片", "悬疑", "剧情", "动画", "美食", "城市"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";

  const [term, setTerm] = useState(keyword);
  const [category, setCategory] = useState<CategoryKey>("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [submitted, setSubmitted] = useState(keyword);

  useEffect(() => {
    setTerm(keyword);
    setSubmitted(keyword);
  }, [keyword]);

  usePageMeta(
    keyword ? `搜索“${keyword}” - 色色视频` : "视频搜索 - 色色视频",
    keyword
      ? `色色视频站内搜索：与“${keyword}”相关的视频内容，可按分类与排序方式进一步筛选。`
      : "在色色视频中按标题、简介、导演或标签检索电影、短片与热门视频内容。",
    `色色视频,视频搜索,${keyword || "在线视频,高清视频"}`,
  );

  const results = useMemo(
    () => queryVideos({ category, keyword: submitted, sort }),
    [category, submitted, sort],
  );

  const loading = useSimulatedLoading(`${submitted}-${category}-${sort}`, 280);
  const hasKeyword = submitted.length > 0;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = term.trim();
    setSubmitted(next);
    setSearchParams(next ? { q: next } : {});
  };

  const handleKeywordPick = (value: string) => {
    setTerm(value);
    setSubmitted(value);
    setSearchParams({ q: value });
    setCategory("all");
  };

  const handleReset = () => {
    setTerm("");
    setSubmitted("");
    setSearchParams({});
    setCategory("all");
    setSort("latest");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="container-page pt-4 md:pt-5">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />
        </div>

        <section className="container-page pt-5">
          <div className="rounded-lg bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 px-5 py-7 md:px-8 md:py-9">
            <p className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent-300">
              Search Library
            </p>
            <h1 className="mt-2 text-2xl font-semibold text-background-50 md:text-[30px]">
              站内视频搜索
            </h1>
            <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-background-300 md:text-sm">
              输入关键词检索视频标题、简介、导演或标签，支持按分类过滤与多种排序方式。
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-5 flex w-full max-w-2xl flex-col gap-2 sm:flex-row"
              role="search"
            >
              <div className="flex flex-1 items-center gap-2 rounded-md bg-background-50 px-3 py-2.5">
                <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true" />
                <input
                  type="search"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="例如：悬疑、短片、城市漫游计划"
                  aria-label="输入搜索关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                {term ? (
                  <button
                    type="button"
                    onClick={() => setTerm("")}
                    aria-label="清空关键词"
                    className="focus-ring inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-line text-sm" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <button
                type="submit"
                className="focus-ring inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                搜索视频
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-background-400">热门搜索：</span>
              {hotKeywords.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => handleKeywordPick(item)}
                  className="focus-ring cursor-pointer rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="container-page pt-6 md:pt-8">
          <div className="flex flex-col gap-3 border-b border-background-200/80 pb-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground-900 md:text-base">
                {hasKeyword ? (
                  <>
                    关键词：
                    <span className="text-secondary-700">“{submitted}”</span>
                  </>
                ) : (
                  "等待输入关键词"
                )}
              </h2>
              {hasKeyword ? (
                <span className="rounded-full bg-background-100 px-2 py-0.5 font-label text-[11px] text-foreground-600">
                  共 {results.length} 个结果
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {categories.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setCategory(item.key)}
                  aria-pressed={category === item.key}
                  className={`focus-ring cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
                    category === item.key
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300/80 bg-white text-foreground-600 hover:border-secondary-300"
                  }`}
                >
                  {item.shortName}
                </button>
              ))}
              <span className="mx-1 hidden h-4 w-px bg-background-300 sm:block" aria-hidden="true" />
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  aria-pressed={sort === option.key}
                  className={`focus-ring cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
                    sort === option.key
                      ? "border-secondary-500 bg-secondary-50 text-secondary-800"
                      : "border-background-300/80 bg-white text-foreground-600 hover:border-secondary-300"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-6">
            {!hasKeyword ? (
              <EmptyState
                icon="ri-search-line"
                title="输入关键词开始搜索"
                description="可以搜索视频标题、简介、导演或标签，例如“悬疑”“动画”“城市”。也可以从下方分类入口直接浏览。"
              >
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {categories
                    .filter((item) => item.key !== "all")
                    .map((item) => (
                      <Link
                        key={item.key}
                        to={`/category/${item.key}`}
                        className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-background-300/80 bg-white px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-700"
                      >
                        <i className={`${item.icon} text-sm`} aria-hidden="true" />
                        浏览{item.name}
                      </Link>
                    ))}
                </div>
              </EmptyState>
            ) : loading ? (
              <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonCard key={`search-skeleton-${index}`} />
                ))}
              </div>
            ) : results.length === 0 ? (
              <EmptyState
                icon="ri-emotion-sad-line"
                title={`没有找到与“${submitted}”相关的内容`}
                description="可以尝试更换关键词、减少筛选条件，或从以下分类中继续浏览。"
              >
                <button
                  type="button"
                  onClick={handleReset}
                  className="focus-ring inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  清空并重新搜索
                </button>
                <Link
                  to="/category/all"
                  className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-background-300/80 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-300"
                >
                  浏览全部视频
                </Link>
              </EmptyState>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {results.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
                <p className="mt-6 text-center text-[11px] text-foreground-400">
                  已展示全部 {results.length} 个匹配结果
                </p>
              </>
            )}
          </div>
        </section>

        <section className="container-page pt-10 md:pt-12">
          <div className="surface-card p-5 md:p-6">
            <h2 className="text-sm font-semibold text-foreground-900 md:text-base">
              按分类继续浏览
            </h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {categories
                .filter((item) => item.key !== "all")
                .map((item) => (
                  <Link
                    key={item.key}
                    to={`/category/${item.key}`}
                    className="focus-ring group flex items-center gap-3 rounded-md border border-background-200/90 bg-background-50 px-3 py-3 transition-colors hover:border-secondary-300"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-primary-600">
                      <i className={`${item.icon} text-base`} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold text-foreground-900">
                        {item.name}
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] text-foreground-500">
                        {item.description}
                      </span>
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}